import { apiErrorDetail } from "@jkast90/kast-common/api-client";
import { useState } from "react";
import {
  Banner,
  Button,
  Dialog,
  Select,
  TextArea,
  TextField,
} from "@jkast90/kast-common/ui";
import { banners, useBanner } from "@common/banner";
import { type Family } from "../../services/kindlyApi";
import styles from "./ProfileDialog.module.css";

const FORM_ID = "profile-form";

interface ProfileData {
  id?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  bio?: string;
  pronouns?: string;
  birthday?: string;
  profile_type?: string;
}

const PROFILE_TYPES = [
  { value: "adult", label: "Adult" },
  { value: "child", label: "Child" },
  { value: "pet", label: "Pet" },
  { value: "memorial", label: "Memorial" },
  { value: "family_member", label: "Family Member" },
  { value: "family_friend", label: "Family Friend" },
  { value: "friend", label: "Friend" },
];

interface ProfileDialogProps {
  open: boolean;
  onClose: () => void;
  /** For a new profile, `familyId` is the family it is made in. */
  onSave: (data: ProfileData, familyId?: number) => Promise<void>;
  profileData?: ProfileData;
  isNew?: boolean;
  currentUserEmail?: string;
  /** New profiles only: the families the user heads, one of which it joins. */
  families?: Family[];
}

export default function ProfileDialog({
  open,
  onClose,
  onSave,
  profileData,
  isNew = false,
  currentUserEmail,
  families = [],
}: ProfileDialogProps) {
  const [formData, setFormData] = useState<ProfileData>({
    first_name: "",
    last_name: "",
    email: "",
    bio: "",
    pronouns: "",
    birthday: "",
    profile_type: "adult",
  });

  const [selectedFamily, setSelectedFamily] = useState<number | null>(null);
  const { showError } = useBanner();

  // Reset the form only when the dialog opens (closed -> open), during render
  // rather than in an effect so it never paints with stale values.
  const [wasOpen, setWasOpen] = useState(false);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open) {
      setSelectedFamily(families.length === 1 ? families[0].id : null);
      if (profileData) {
        // Only the form's fields: the picture is changed on the profile page
        // (Change picture), and the rest of the record is not this form's to send.
        const {
          first_name,
          last_name,
          email,
          bio,
          pronouns,
          birthday,
          profile_type,
        } = profileData;
        setFormData({
          id: profileData.id,
          first_name,
          last_name,
          email,
          bio,
          pronouns,
          birthday,
          profile_type,
        });
      } else {
        setFormData({
          first_name: "",
          last_name: "",
          email: "",
          bio: "",
          pronouns: "",
          birthday: "",
          profile_type: "adult",
        });
      }
    }
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTypeChange = (value: string | null) => {
    const profileType = value ?? "adult";
    setFormData((prev) => ({ ...prev, profile_type: profileType }));

    // Auto-fill email when "child" profile type is selected
    if (profileType === "child" && isNew && currentUserEmail) {
      handleUseMyEmail();
    }
  };

  const handleUseMyEmail = () => {
    if (currentUserEmail) {
      let emailToUse = currentUserEmail;

      // If there's a first name entered, use plus addressing
      if (formData.first_name?.trim()) {
        const [localPart, domain] = currentUserEmail.split("@");
        if (domain) {
          // Add the first name after a plus sign
          emailToUse = `${localPart}+${formData.first_name.trim()}@${domain}`;
        }
      }

      setFormData((prev) => ({
        ...prev,
        email: emailToUse,
      }));
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    // Validate required fields
    if (!formData.first_name?.trim()) {
      showError("First name is required");
      return;
    }
    if (!formData.last_name?.trim()) {
      showError("Last name is required");
      return;
    }
    if (!formData.birthday) {
      showError("Birthday is required");
      return;
    }
    if (isNew && selectedFamily === null) {
      showError("Choose the family this profile belongs to");
      return;
    }

    try {
      await onSave(formData, isNew ? (selectedFamily ?? undefined) : undefined);
      // Close dialog on successful save
      onClose();
    } catch (error: unknown) {
      showError(apiErrorDetail(error, "Failed to save profile"));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={isNew ? "Create Profile" : "Edit Profile"}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form={FORM_ID}>
            {isNew ? "Create" : "Save"}
          </Button>
        </>
      }
    >
      <Banner store={banners} />

      <form id={FORM_ID} onSubmit={handleSubmit} className="kui-form-grid">
        <TextField
          label="First Name"
          name="first_name"
          value={formData.first_name || ""}
          onChange={handleChange}
          required
        />
        <TextField
          label="Last Name"
          name="last_name"
          value={formData.last_name || ""}
          onChange={handleChange}
          required
        />
        <Select
          label="Profile Type"
          name="profile_type"
          required
          options={PROFILE_TYPES}
          value={formData.profile_type || "adult"}
          onChange={handleTypeChange}
        />
        <div className={styles.emailInputGroup}>
          <TextField
            label={isNew ? "Email" : "Email (optional)"}
            type="email"
            name="email"
            value={formData.email || ""}
            onChange={handleChange}
            disabled={!isNew}
            placeholder={isNew ? 'Click "Use Mine" for kids profiles' : ""}
            wrapperClassName={styles.emailField}
          />
          {isNew && currentUserEmail && (
            <Button
              onClick={handleUseMyEmail}
              className={styles.useMyEmailButton}
            >
              Use Mine
            </Button>
          )}
        </div>
        <TextField
          label="Pronouns"
          name="pronouns"
          value={formData.pronouns || ""}
          onChange={handleChange}
          placeholder="e.g. he/him, she/her, they/them"
        />
        <TextField
          label="Birthday"
          type="date"
          name="birthday"
          value={formData.birthday || ""}
          onChange={handleChange}
          required
        />

        <TextArea
          wrapperClassName="kui-form-span"
          label="Bio"
          name="bio"
          value={formData.bio || ""}
          onChange={handleChange}
          rows={4}
        />

        {isNew && (
          <Select
            wrapperClassName="kui-form-span"
            label="Family"
            required
            placeholder="Choose a family you head"
            options={families.map((family) => ({
              value: String(family.id),
              label: family.name,
            }))}
            value={selectedFamily === null ? null : String(selectedFamily)}
            onChange={(value) =>
              setSelectedFamily(value ? Number(value) : null)
            }
            hint="Only your family sees this profile. Someone with their own account joins by invitation instead."
          />
        )}
      </form>
    </Dialog>
  );
}
